import React, {useEffect, useState} from "react";
import { useApi } from "./UserProvider";

export const TasksContext = React.createContext({
    tasks: [],
    tasksLoading: true,
    handleTaskEdit: () => {}  
});

export const TasksProvider = ({children}) => {
    
    const { api } = useApi(); 
    const [tasks, setTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(true);

    useEffect(() => {
        setTasksLoading(true);

        const fetchTasks = async () => {
            try {
                const tsk = await api.request("tasks/query/all");
                const parsedTasks = tsk.map(task => ({
                    ...task,
                    taskStart: (new Date(task.taskStart)),
                    taskDeadline: (new Date(task.taskDeadline)),
                    taskFinished: task.taskFinished ? (new Date(task.taskFinished)) : null,
                }));
                

                setTasks(parsedTasks);
            }
            catch(err)
            {
                console.error(err);
            }
            finally
            {
                setTasksLoading(false);
            }
        };

        fetchTasks();
    }, [api]);

    const handleTasksEdit = async ({taskUuid, action, changes}) => {

        const previousTasks = structuredClone(tasks);

        if(action === "CREATE TASK")
        {
            const newTask = {
                taskUuid,
                taskTitle: changes.taskTitle,
                taskStatus: "NotStarted",
                taskDescription: changes.taskDescription,
                taskStart: changes.taskStart,
                taskDeadline: changes.taskDeadline,
                taskFinished: null,
                taskDuration: changes.taskDuration,
                taskPriority: changes.taskPriority,
                taskUndertasks: changes.taskUndertasks,
                taskDelivery: changes.taskDelivery,
                taskProjectUuid: changes.taskProjectUuid
            };

            const uiTask = {
                ...newTask
            };

            const apiTask = {
                ...newTask,
                taskDescription: changes.taskDescription,
                taskStart: newTask.taskStart.toISOString().slice(0, 10),
                taskDeadline: newTask.taskDeadline.toISOString().slice(0, 10)
            };

            setTasks([uiTask, ...tasks]);

            try
            {
                await api.request("tasks/create", apiTask);
                return ({
                    status: "TASK_CREATED"
                })
            }
            catch(err)
            {
                console.error(err);
                setTasks(previousTasks);
                return ({
                    status: "TASK_NOT_CREATED",
                    reason: "SERVER_ERROR"
                })
            }
        }
        else if(action === "UPDATE TASK")
        {
            const taskCopyBase = structuredClone(tasks.find(tsk => tsk.taskUuid === taskUuid));
            if(!taskCopyBase) return;

            const taskCopy = {
                ...taskCopyBase,
                taskTitle: changes.taskTitle,
                taskDescription: changes.taskDescription ?? taskCopyBase.taskDescription,
                taskStart: changes.taskStart,
                taskDeadline: changes.taskDeadline,
                taskDuration: changes.taskDuration,
                taskPriority: changes.taskPriority,
                taskDelivery: changes.taskDelivery,
                taskUndertasks: changes.taskUndertasks,
                taskProjectUuid: changes.taskProjectUuid
            };

            const otherTasks = tasks.filter(tsk => tsk.taskUuid !== taskUuid);

            setTasks([taskCopy, ...otherTasks]);

            const apiTask = {
                ...taskCopy,
                taskDescription: taskCopy.taskDescription,
                taskStart: taskCopy.taskStart.toISOString().slice(0, 10),
                taskDeadline: taskCopy.taskDeadline.toISOString().slice(0, 10),
                taskFinished: taskCopy.taskFinished
                    ? taskCopy.taskFinished.toISOString().slice(0, 10)
                    : null
            };

            try
            {
                await api.request("tasks/update", apiTask);

                return ({
                    status: "TASK_UPDATED"
                });
            }
            catch (err)
            {
                console.error(err);
                setTasks(previousTasks);

                return ({
                    status: "TASK_NOT_UPDATED",
                    reason: "SERVER_ERROR"
                })
            }
        }
        else if(action === "STATUS CHANGE")
        {
            const taskCopyBase = structuredClone(tasks.find(tsk => tsk.taskUuid === taskUuid));
            if(!taskCopyBase) return;

            const taskCopy = { ...taskCopyBase };

            taskCopy.taskStatus = changes.newStatus;

            const otherTasks = tasks.filter(tsk => tsk.taskUuid !== taskUuid);

            if(changes.newStatus === "NotStarted" || changes.newStatus === "Started" || changes.newStatus === "Stopped")
            {
                taskCopy.taskFinished = null;
            }
            else if(changes.newStatus === "Finished")
            {
                taskCopy.taskFinished = new Date();
            }

            const isArchived = changes.newStatus === "Archived";

            const uiTasks = isArchived
                ? otherTasks
                : [...otherTasks, taskCopy];

            setTasks(uiTasks);

            const apiTask = {
                ...taskCopy,
                taskStart: taskCopy.taskStart.toISOString().slice(0, 10),
                taskDeadline: taskCopy.taskDeadline.toISOString().slice(0, 10),
                taskFinished: taskCopy.taskFinished
                    ? taskCopy.taskFinished.toISOString().slice(0, 10)
                    : null
            };

            try
            {
                await api.request("tasks/update", apiTask);

                if(isArchived)
                {
                    return ({
                        status: "TASK_ARCHIVED",
                        reason: "TASK_ARCHIVED"
                    });
                }

                return ({
                    status: "TASK_UPDATED"
                });
            }
            catch (err)
            {
                console.error(err);
                setTasks(previousTasks);

                return ({
                    status: "TASK_NOT_UPDATED",
                    reason: "SERVER_ERROR"
                });
            }
        }
    }

    return (
        <TasksContext.Provider value = {{tasks: tasks, tasksLoading: tasksLoading, handleTasksEdit: handleTasksEdit}}>
            { children}
        </TasksContext.Provider>
    )
}